import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const Testimonial = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    rating: '5',
    message: '',
    name: '',
    phone: '',
    userImage: ''
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('testimonialList');
      if (response.data) {
        setTestimonials(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id) => {
    try {
      const response = await apiService.get(`testimonialStatus/${id}`);
      if (response.status) {
        toast.success(response.message);
        setTestimonials(prev => prev.map(t => 
          t._id === id ? { ...t, status: t.status === 1 ? 0 : 1 } : t
        ));
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    }
  };

  const handleLockedChange = async (id) => {
    try {
      const response = await apiService.get(`testimonialLocked/${id}`);
      if (response.status) {
        toast.success(response.message);
        setTestimonials(prev => prev.map(t => 
          t._id === id ? { ...t, locked: !t.locked } : t
        ));
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      id: testimonial._id,
      rating: testimonial.rating || '5',
      message: testimonial.message || '',
      name: testimonial.userId?.first_name || '',
      phone: testimonial.userId?.phone || '',
      userImage: testimonial.userId?.image || ''
    });
    setImageFile(null);
    setImagePreview(testimonial.userId?.image ? `${process.env.REACT_APP_MEDIA_URL}${testimonial.userId.image}` : null);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setFormData({
      id: '',
      rating: '5',
      message: '',
      name: '',
      phone: '',
      userImage: ''
    });
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let uploadedImageUrl = formData.userImage;
      if (imageFile) {
        const uploadRes = await apiService.uploadFile(imageFile);
        if (uploadRes.status && uploadRes.data && uploadRes.data.image) {
          uploadedImageUrl = uploadRes.data.image;
        }
      }

      const submitPayload = {
        ...formData,
        userImage: uploadedImageUrl
      };

      const response = await apiService.post('updateTestimonial', submitPayload);
      if (response.duplicateTestimonial) {
        const confirmUpdate = window.confirm(response.message);
        if (confirmUpdate) {
          setLoading(true);
          const forceResponse = await apiService.post('updateTestimonial', {
            ...submitPayload,
            forceUpdate: true
          });
          if (forceResponse.status) {
            toast.success('Testimonial updated successfully');
            setShowModal(false);
            fetchTestimonials();
          } else {
            toast.error(forceResponse.message || 'Failed to update testimonial');
          }
        }
      } else {
        if (response.status || response.success) {
          toast.success('Testimonial updated successfully');
          setShowModal(false);
          fetchTestimonials();
        } else {
          toast.error(response.message || 'Failed to update testimonial');
        }
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`deleteTestimonial/${deleteId}`);
      if (response.status || response.success) {
        toast.success('Testimonial deleted successfully');
        setShowDeleteModal(false);
        fetchTestimonials();
      } else {
        toast.error(response.message || 'Failed to delete testimonial');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-0">
        {/* Breadcrumb */}
        <div className="mb-3">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <a href="/dashboard" className="hover:text-gray-900">Home</a>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900">Testimonial</span>
          </nav>
        </div>

        {/* Card */}
        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Testimonial</h3>
            <button
              onClick={handleAddNew}
              className="flex items-center px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#009ef7' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0084d4'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#009ef7'}
            >
              <i className="bi bi-plus text-sm mr-2"></i>
              Add New
            </button>
          </div>

          {/* Table */}
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-sm">
                    <th className="px-4 py-3 text-left font-medium min-w-[320px]">User Details</th>
                    <th className="px-4 py-3 text-left font-medium">Message</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Locked</th>
                    <th className="px-4 py-3 text-center font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : testimonials.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        No testimonials found
                      </td>
                    </tr>
                  ) : (
                    testimonials.map((testimonial) => {
                      const userName = [testimonial.userId?.first_name, testimonial.userId?.last_name].filter(Boolean).join(' ') || 'User';

                      return (
                        <tr key={testimonial._id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 min-w-[320px]">
                            <div className="flex items-center gap-3">
                              {/* Avatar Image */}
                              <div className="shrink-0">
                                {testimonial.userId?.image ? (
                                  <img
                                    src={`${process.env.REACT_APP_MEDIA_URL}${testimonial.userId.image}`}
                                    alt={userName}
                                    className="w-10 h-10 rounded-full object-cover border border-gray-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className="w-10 h-10 rounded-full bg-slate-100 border border-gray-200 flex items-center justify-center"
                                  style={{ display: testimonial.userId?.image ? 'none' : 'flex' }}
                                >
                                  <i className="bi bi-person text-slate-400 text-lg"></i>
                                </div>
                              </div>

                              {/* User Info Details */}
                              <div className="flex flex-col gap-0.5 text-xs">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <a
                                    href={`/view-profile/${testimonial.userId?._id}`}
                                    className="font-semibold text-gray-900 hover:text-[#009ef7] hover:underline"
                                  >
                                    {userName}
                                  </a>
                                  {testimonial.userId?.role && (
                                    <span className="text-[10px] px-1.5 py-0.2 bg-gray-100 text-gray-600 rounded font-semibold uppercase">
                                      {testimonial.userId.role}
                                    </span>
                                  )}
                                </div>

                                {testimonial.userId?.business_name && (
                                  <span className="text-gray-700 font-medium">
                                    🏢 {testimonial.userId.business_name}
                                  </span>
                                )}

                                {testimonial.userId?.phone && (
                                  <span className="text-gray-500 font-normal">
                                    📞 {testimonial.userId.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-sm text-gray-700">
                            {testimonial.rating && (
                              <div className="flex items-center gap-1 text-amber-500 font-semibold text-xs mb-1">
                                <span>⭐ {testimonial.rating} / 5</span>
                              </div>
                            )}
                            <div className="text-gray-800">{testimonial.message}</div>
                          </td>
                        <td className="px-4 py-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={testimonial.status === 1}
                              onChange={() => handleStatusChange(testimonial._id)}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                          </label>
                        </td>
                        <td className="px-4 py-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={testimonial.locked === true}
                              onChange={() => handleLockedChange(testimonial._id)}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                          </label>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => handleEdit(testimonial)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            >
                              <i className="bi bi-pencil text-lg"></i>
                            </button>
                            <button
                              onClick={() => openDeleteModal(testimonial._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <i className="bi bi-trash text-lg"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-[4px] flex items-center justify-center z-50 p-4 transition-all duration-300 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-full max-w-lg overflow-hidden transform transition-all duration-300 scale-100 flex flex-col">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50/40">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <i className={`bi ${formData.id ? 'bi-pencil-square text-indigo-500' : 'bi-plus-circle text-emerald-500'} text-base`}></i>
                <span>{formData.id ? 'Edit Testimonial' : 'Create Testimonial'}</span>
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-colors duration-200 focus:outline-none"
              >
                <i className="bi bi-x-lg text-[10px]"></i>
              </button>
            </div>
            <div className="px-5 pt-3.5 pb-5">
              <form onSubmit={handleSubmit}>
                {formData.id ? (
                  <div className="grid grid-cols-[auto_1fr] gap-4 items-start mb-3">
                    {/* Square Image Column */}
                    <div className="flex flex-col items-center shrink-0">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 self-start">
                        Photo
                      </label>
                      <div className="relative w-24 h-24 border border-dashed border-slate-300 hover:border-indigo-400 rounded-xl overflow-hidden bg-slate-50 flex items-center justify-center cursor-pointer group transition-all duration-200 shadow-sm hover:shadow">
                        {imagePreview ? (
                          <>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setImageFile(null);
                                setImagePreview(null);
                                setFormData({ ...formData, userImage: '' });
                              }}
                              className="absolute top-1 right-1 w-5 h-5 bg-rose-500 rounded-md text-white flex items-center justify-center hover:bg-rose-600 transition-colors z-10 border border-white"
                              title="Remove image"
                            >
                              <i className="bi bi-x text-xs"></i>
                            </button>
                          </>
                        ) : (
                          <i className="bi bi-camera text-slate-400 text-xl group-hover:text-slate-600 transition-colors"></i>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setImageFile(file);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setImagePreview(reader.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                        Rating <span className="text-rose-500">*</span>
                      </label>
                      <div className="flex items-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFormData({ ...formData, rating: String(star) })}
                            className="focus:outline-none transition-transform duration-150 hover:scale-110"
                          >
                            <i className={`bi bi-star-fill text-xl transition-colors duration-200 ${
                              star <= Number(formData.rating || 5) ? 'text-amber-400' : 'text-slate-200'
                            }`}></i>
                          </button>
                        ))}
                        <span className="text-xs font-bold text-slate-500 ml-2">
                          ({formData.rating || 5} / 5)
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-[auto_1fr] gap-4 items-start mb-3">
                      {/* Square Image Column */}
                      <div className="flex flex-col items-center shrink-0">
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 self-start">
                          Photo
                        </label>
                        <div className="relative w-24 h-24 border border-dashed border-slate-300 hover:border-indigo-400 rounded-xl overflow-hidden bg-slate-50 flex items-center justify-center cursor-pointer group transition-all duration-200 shadow-sm hover:shadow">
                          {imagePreview ? (
                            <>
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setImageFile(null);
                                  setImagePreview(null);
                                }}
                                className="absolute top-1 right-1 w-5 h-5 bg-rose-500 rounded-md text-white flex items-center justify-center hover:bg-rose-600 transition-colors z-10 border border-white"
                                title="Remove image"
                              >
                                <i className="bi bi-x text-xs"></i>
                              </button>
                            </>
                          ) : (
                            <i className="bi bi-camera text-slate-400 text-xl group-hover:text-slate-600 transition-colors"></i>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setImageFile(file);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setImagePreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                          />
                        </div>
                      </div>

                      {/* Name & Phone Stack Column */}
                      <div className="flex flex-col space-y-3 w-full">
                        {/* Name Input */}
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                            Name <span className="text-rose-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                              <i className="bi bi-person text-sm"></i>
                            </span>
                            <input
                              type="text"
                              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 bg-slate-50/30 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition-all duration-200 font-semibold"
                              placeholder="Enter traveler's name"
                              value={formData.name || ''}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              required
                            />
                          </div>
                        </div>

                        {/* Phone Input */}
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                            Phone <span className="text-rose-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                              <i className="bi bi-telephone text-sm"></i>
                            </span>
                            <input
                              type="text"
                              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 bg-slate-50/30 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition-all duration-200 font-semibold"
                              placeholder="Enter phone number"
                              value={formData.phone || ''}
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Rating Row */}
                    <div className="mb-3">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                        Rating <span className="text-rose-500">*</span>
                      </label>
                      <div className="flex items-center space-x-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFormData({ ...formData, rating: String(star) })}
                            className="focus:outline-none transition-transform duration-150 hover:scale-110"
                          >
                            <i className={`bi bi-star-fill text-xl transition-colors duration-200 ${
                              star <= Number(formData.rating || 5) ? 'text-amber-400' : 'text-slate-200'
                            }`}></i>
                          </button>
                        ))}
                        <span className="text-xs font-bold text-slate-500 ml-2">
                          ({formData.rating || 5} / 5)
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <div className="mb-3">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                    Review Message <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    rows="5"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 bg-slate-50/30 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition-all duration-200 font-medium leading-relaxed"
                    placeholder="Write custom testimonial comment..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                  />
                </div>
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-100 bg-slate-50/30 px-5 py-3.5 -mx-5 -mb-5">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all duration-150"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 text-xs font-bold text-white bg-[#009ef7] hover:bg-[#0084d4] rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 transition-all duration-200 flex items-center gap-1.5"
                  >
                    {loading ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg text-sm"></i>
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirmation</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to delete this Testimonial?</p>
            </div>
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-2 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-2 sm:px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                {loading ? 'Please wait...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testimonial;
